import clsx from 'clsx';
import type { PropsWithChildren } from 'react';
import { Modal as ReactModal } from 'react-bootstrap';
import { ModalBody } from './ModalBody';
import { ModalContext } from './ModalContext';
import { ModalFooter } from './ModalFooter';
import { ModalHeader } from './ModalHeader';

/**
 * https://developer.sgmarkets.com/docs/design-system/v5/components/modals.html
 *
 * https://designlab.sgmarkets.com/component-viewer/modal?sgbsVersion=5&full+screen=false&footer=false&alignment=Center
 */
type ModalProps = {
  show: boolean;
  onClose: () => void;

  purpose?: 'default' | 'contextual';
  color?: 'primary' | 'info' | 'success' | 'warning' | 'danger';
  size?: 'sm' | 'lg' | 'xl';
  border?: boolean;
  shadow?: boolean;
  backdrop?: boolean | 'static';
  alignment?: 'center' | 'top';
  fullScreen?: boolean;
  scrolling?: boolean;
  className?: string;
};

/**
 * This is the docs descriptions
 * @param props
 * @returns
 */
export const Modal = (props: PropsWithChildren<ModalProps>) => {
  const {
    show = false,
    onClose,
    purpose = 'default',
    color = 'primary',
    size,
    border = false,
    shadow = true,
    backdrop = true,
    alignment = 'center',
    fullScreen = false,
    scrolling = false,
    className,
    children,
  } = props;

  if (!show) {
    return null;
  }

  return (
    <ReactModal
      show={show}
      onHide={onClose}
      backdrop={backdrop}
      size={size}
      fullscreen={fullScreen ? true : undefined}
      scrollable={scrolling}
      className={clsx('border', className, {
        'modal-top': alignment === 'top',
      })}
      contentClassName={clsx('border', {
        'border-primary': border && color === 'primary',
        'border-info': border && color === 'info',
        'border-success': border && color === 'success',
        'border-warning': border && color === 'warning',
        'border-danger': border && color === 'danger',
        'shadow-max': shadow,
        'modal-top': alignment === 'top',
      })}
    >
      <ModalContext.Provider
        value={{
          show,
          onClose,
          purpose,
          color,
          size,
          border,
          shadow,
          backdrop,
          alignment,
          fullScreen,
          scrolling,
        }}
      >
        {children}
      </ModalContext.Provider>
    </ReactModal>
  );
};

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
